import { useState } from "react";
import { useDataProvider, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { CrmDataProvider } from "../providers/types";
import type { SalesTool } from "../types";

/** Only http(s) links are ever rendered as anchors. */
const isSafeUrl = (value: string) => /^https?:\/\//i.test(value);

/**
 * Detail view of one tool: its text, its video link, or its file opened through a short-lived
 * signed URL in a new tab (never a public link).
 */
export const SalesToolDetail = ({
  tool,
  onClose,
}: {
  tool: SalesTool | null;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [fileError, setFileError] = useState(false);

  const openFile = async () => {
    if (!tool) return;
    setFileError(false);
    // Opened synchronously so the browser does not block the popup after the await.
    const tab = window.open("about:blank", "_blank");
    try {
      const url = await dataProvider.getSalesToolFileUrl(tool);
      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, "_blank");
      }
    } catch {
      tab?.close();
      setFileError(true);
    }
  };

  return (
    <Dialog open={tool != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-9/10 overflow-y-auto">
        {tool ? (
          <>
            <DialogHeader>
              <DialogTitle>{tool.title}</DialogTitle>
              <p className="text-sm text-muted-foreground">
                {translate(`crm.salesTools.kinds.${tool.kind}`)}
              </p>
            </DialogHeader>
            {tool.body ? (
              <p
                className="whitespace-pre-line text-sm"
                data-testid="sales-tool-body"
              >
                {tool.body}
              </p>
            ) : null}
            {tool.video_url ? (
              isSafeUrl(tool.video_url) ? (
                <a
                  href={tool.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm underline underline-offset-2"
                >
                  {translate("crm.salesTools.open_link")}
                </a>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {tool.video_url}
                </p>
              )
            ) : null}
            {tool.storage_path ? (
              <div className="space-y-2">
                <Button type="button" variant="outline" onClick={openFile}>
                  {translate("crm.salesTools.open_file", {
                    name: tool.file_name ?? "",
                  })}
                </Button>
                {fileError ? (
                  <p role="alert" className="text-sm text-destructive">
                    {translate("crm.salesTools.file_error")}
                  </p>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
